import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_URL } from '../lib/utils';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { UploadCloud, X, FileText, Sparkles, AlertTriangle, Trash2, CheckCircle2 } from 'lucide-react';

/**
 * Upload a Resource List — AI document extraction.
 * Admins: get an editable preview, then commit selected rows to the review queue.
 * Members: extraction is queued immediately for admin review.
 */
const UploadResourceListModal = ({
  categories = [],
  isAdmin = false,
  triggerClassName = 'bg-white text-primary hover:bg-white/90 font-semibold',
  onCommitted,
}) => {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [records, setRecords] = useState(null); // admin preview rows
  const [sourceFile, setSourceFile] = useState('');
  const [done, setDone] = useState(null); // member success summary
  const [email, setEmail] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const reset = () => { setFile(null); setRecords(null); setSourceFile(''); setDone(null); setEmail(''); };
  const close = () => { setOpen(false); reset(); };

  const extract = async () => {
    if (!file) { toast.error('Please choose a file first.'); return; }
    setUploading(true);
    setDone(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('company_url', ''); // honeypot — stays empty for real users
      if (email.trim()) fd.append('submitter_email', email.trim());
      const { data } = await axios.post(`${API_URL}/resources/upload-list`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSourceFile(data.source_file || file.name);
      if (data.mode === 'preview') {
        setRecords((data.records || []).map((r) => ({ ...r, _keep: true })));
      } else {
        setDone(data);
        toast.success(data.message || 'Submitted for review.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not process that file.');
    } finally {
      setUploading(false);
    }
  };

  const setRow = (idx, key, val) => setRecords((rs) => rs.map((r, i) => (i === idx ? { ...r, [key]: val } : r)));

  const commit = async () => {
    const kept = (records || []).filter((r) => r._keep && (r.name || '').trim());
    if (kept.length === 0) { toast.error('Keep at least one resource to add.'); return; }
    setCommitting(true);
    try {
      const payload = kept.map(({ _keep, category, ...r }) => r); // eslint-disable-line no-unused-vars
      const { data } = await axios.post(`${API_URL}/resources/upload-list/commit`, {
        source_file: sourceFile, records: payload,
      });
      toast.success(data.message || 'Added to review queue.');
      close();
      if (onCommitted) onCommitted();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not add to the queue.');
    } finally {
      setCommitting(false);
    }
  };

  const keptCount = (records || []).filter((r) => r._keep).length;

  return (
    <>
      <Button onClick={() => setOpen(true)} data-testid="upload-resource-list-btn" className={triggerClassName}>
        <UploadCloud className="w-4 h-4 mr-2" /> Upload a Resource List
      </Button>

      {open && (
        <div className="fixed inset-0 z-[100] flex items-start justify-center bg-black/50 p-4 overflow-y-auto" data-testid="upload-resource-list-modal">
          <div className="bg-white text-gray-900 rounded-2xl w-full max-w-3xl my-8 shadow-2xl">
            <div className="flex items-center justify-between p-5 border-b sticky top-0 bg-white rounded-t-2xl z-10">
              <div>
                <h3 className="font-serif text-xl font-bold text-primary flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-secondary" /> Upload a Resource List
                </h3>
                <p className="text-sm text-gray-500">
                  Upload a PDF, Word, Excel, or CSV list of organizations. Our AI extracts each one automatically.
                  {isAdmin ? ' Review and edit the results before they enter the queue.' : ' Everything is reviewed by our team before it appears publicly.'}
                </p>
              </div>
              <button onClick={close} data-testid="upload-resource-list-close" className="text-gray-400 hover:text-gray-700"><X className="w-5 h-5" /></button>
            </div>

            <div className="p-5 space-y-5">
              {/* Success summary (member flow) */}
              {done ? (
                <div className="text-center py-8" data-testid="upload-done">
                  <CheckCircle2 className="w-14 h-14 text-emerald-600 mx-auto mb-3" />
                  <p className="text-lg font-semibold text-primary">{done.queued} resource(s) submitted for review</p>
                  <p className="text-sm text-gray-500 mt-1">
                    Extracted from <span className="font-medium">{sourceFile}</span>.
                    {done.possible_duplicates > 0 && ` ${done.possible_duplicates} may already be listed — our team will check.`}
                  </p>
                  <Button className="mt-5 bg-primary text-white" onClick={close} data-testid="upload-done-close">Done</Button>
                </div>
              ) : (
                <>
                  {/* File picker */}
                  <div>
                    <label
                      htmlFor="resource-list-file"
                      className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-xl p-6 cursor-pointer hover:border-primary/50 transition-colors"
                      data-testid="upload-dropzone"
                    >
                      <FileText className="w-8 h-8 text-gray-400 mb-2" />
                      <span className="text-sm text-gray-600">{file ? file.name : 'Click to choose a file (PDF, Word, Excel, CSV — up to 10 MB)'}</span>
                      <input
                        id="resource-list-file" ref={inputRef} type="file" className="hidden"
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
                        onChange={(e) => { setFile(e.target.files?.[0] || null); setRecords(null); }}
                        data-testid="upload-file-input"
                      />
                    </label>
                    {!records && (
                      <div className="mt-3 space-y-3">
                        {!isAdmin && (
                          <div>
                            <Label className="text-xs text-gray-500">Your email <span className="text-gray-400">(optional — we'll confirm once it's reviewed)</span></Label>
                            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="upload-submitter-email" />
                          </div>
                        )}
                        <div className="flex justify-end">
                          <Button onClick={extract} disabled={uploading || !file} className="bg-secondary hover:bg-secondary-hover text-white" data-testid="upload-extract-btn">
                            <Sparkles className="w-4 h-4 mr-2" /> {uploading ? 'Extracting…' : 'Extract Resources'}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Admin editable preview */}
                  {records && (
                    <div data-testid="upload-preview">
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-sm font-semibold text-gray-700">
                          {records.length} extracted · {keptCount} selected
                        </p>
                        <p className="text-xs text-gray-400">Uncheck any row to discard it</p>
                      </div>
                      <div className="space-y-3 max-h-[45vh] overflow-y-auto pr-1">
                        {records.map((r, idx) => (
                          <div key={idx} className={`border rounded-xl p-3 ${r._keep ? '' : 'opacity-50 bg-gray-50'}`} data-testid={`preview-row-${idx}`}>
                            <div className="flex items-start justify-between gap-2 mb-2">
                              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
                                <input type="checkbox" checked={r._keep} onChange={(e) => setRow(idx, '_keep', e.target.checked)} data-testid={`preview-keep-${idx}`} />
                                Include
                              </label>
                              {r.possible_duplicate && (
                                <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full flex items-center gap-1" data-testid={`preview-dup-${idx}`}>
                                  <AlertTriangle className="w-3 h-3" /> Possible duplicate{r.duplicate_of?.name ? `: ${r.duplicate_of.name}` : ''}
                                </span>
                              )}
                            </div>
                            <div className="grid sm:grid-cols-2 gap-2">
                              <div className="sm:col-span-2">
                                <Label className="text-xs text-gray-500">Name</Label>
                                <Input value={r.name || ''} onChange={(e) => setRow(idx, 'name', e.target.value)} data-testid={`preview-name-${idx}`} />
                              </div>
                              <div>
                                <Label className="text-xs text-gray-500">Category</Label>
                                <select value={r.category_slug || ''} onChange={(e) => setRow(idx, 'category_slug', e.target.value)}
                                  className="mt-1 w-full h-9 rounded-md border border-gray-300 bg-white px-2 text-sm" data-testid={`preview-cat-${idx}`}>
                                  {categories.map((c) => <option key={c.slug} value={c.slug}>{c.label}</option>)}
                                </select>
                              </div>
                              <div>
                                <Label className="text-xs text-gray-500">Phone</Label>
                                <Input value={r.phone || ''} onChange={(e) => setRow(idx, 'phone', e.target.value)} />
                              </div>
                              <div className="sm:col-span-2">
                                <Label className="text-xs text-gray-500">Website</Label>
                                <Input value={r.website || ''} onChange={(e) => setRow(idx, 'website', e.target.value)} />
                              </div>
                              <div><Label className="text-xs text-gray-500">City</Label><Input value={r.city || ''} onChange={(e) => setRow(idx, 'city', e.target.value)} /></div>
                              <div><Label className="text-xs text-gray-500">State</Label><Input value={r.state || ''} onChange={(e) => setRow(idx, 'state', e.target.value)} /></div>
                              <div className="sm:col-span-2">
                                <Label className="text-xs text-gray-500">Description</Label>
                                <Textarea rows={2} value={r.description || ''} onChange={(e) => setRow(idx, 'description', e.target.value)} />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="flex justify-between items-center gap-2 pt-4 border-t mt-4">
                        <Button variant="outline" onClick={() => { setRecords(null); setFile(null); if (inputRef.current) inputRef.current.value = ''; }} data-testid="upload-start-over">
                          <Trash2 className="w-4 h-4 mr-1" /> Start over
                        </Button>
                        <Button onClick={commit} disabled={committing || keptCount === 0} className="bg-primary hover:bg-primary-hover text-white" data-testid="upload-commit-btn">
                          {committing ? 'Adding…' : `Add ${keptCount} to Review Queue`}
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UploadResourceListModal;
